<template>
	<section class="section-card chart-card" v-loading="loading">
		<div class="section-head section-head--compact">
			<h2 class="section-title">流量温度趋势</h2>
			<el-date-picker
				v-model="selectDate"
				class="time-range-picker"
				type="date"
				value-format="yyyy-MM-dd"
				placeholder="选择日期"
				:clearable="false"
			></el-date-picker>
		</div>

		<div class="chart-shell">
			<div class="chart-legend">
				<div class="chart-legend__item">
					<span class="chart-legend__line chart-legend__line--dashed"></span>
					<span>温度</span>
				</div>
				<div class="chart-legend__item">
					<span class="chart-legend__line"></span>
					<span>流量</span>
				</div>
			</div>
			<div class="chart-legend chart-legend--devices">
				<div v-for="item in chartData.deviceLegendItems" :key="item.name" class="chart-legend__item">
					<span class="chart-legend__dot" :style="{ background: item.color }"></span>
					<span>{{ item.name }}</span>
				</div>
			</div>

			<div ref="chartRef" class="chart-canvas"></div>
		</div>
	</section>
</template>

<script>
const echarts = require('echarts/lib/echarts')
import { apiGetPressureRegulatorFlowTemperatureTrend } from '@/apis/pressureRegulatorManage.api'
require('echarts/lib/chart/line')
require('echarts/lib/chart/bar')
require('echarts/lib/component/grid')
require('echarts/lib/component/tooltip')
require('echarts/lib/component/title')

export default {
	name: 'FlowTrendChart',
	props: {
		collectorDeviceNo: {
			type: [String, Number],
			default: '',
		},
	},
	data() {
		return {
			selectDate: this.dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
			loading: false,
			chartInstance: null,
			chartData: {
				gridTicks: [0, 40, 80, 120, 160, 200],
				labels: [],
				deviceLegendItems: [],
				series: [],
			},
		}
	},
	watch: {
		collectorDeviceNo: {
			handler(value) {
				if (value) {
					this.getFlowTemperatureTrend()
				}
			},
			immediate: true,
		},
		selectDate() {
			this.getFlowTemperatureTrend()
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.initChart()
			window.addEventListener('resize', this.handleResize)
		})
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize)
		if (this.chartInstance) {
			this.chartInstance.dispose()
			this.chartInstance = null
		}
	},
	methods: {
		initChart() {
			if (!this.$refs.chartRef) {
				return
			}

			if (!this.chartInstance) {
				this.chartInstance = echarts.init(this.$refs.chartRef, null, { renderer: 'canvas' })
			}

			this.renderChart()
		},
		handleResize() {
			if (this.chartInstance) {
				this.chartInstance.resize()
			}
		},
		async getFlowTemperatureTrend() {
			if (!this.collectorDeviceNo) {
				this.setChartData([])
				return
			}
			this.loading = true
			try {
				const records = await apiGetPressureRegulatorFlowTemperatureTrend({
					collectorDeviceNo: this.collectorDeviceNo,
					sampleType: 'HOUR',
					...this.getTimeRange(),
				})
				this.setChartData(records || [])
			} finally {
				this.loading = false
			}
		},
		getTimeRange() {
			return {
				startTime: this.selectDate ? `${this.selectDate} 00:00:00` : undefined,
				endTime: this.selectDate ? `${this.selectDate} 23:59:59` : undefined,
			}
		},
		setChartData(records) {
			const timeSet = new Set()
			const deviceColors = ['#3b82f6', '#10b981', '#f97316', '#a855f7', '#14b8a6', '#f43f5e', '#64748b']
			const deviceSeries = []
			const deviceLegendItems = []

			records.forEach((device, index) => {
				const temperatureRecords = Array.isArray(device.temperatureRecords) ? device.temperatureRecords : []
				const flowRecords = Array.isArray(device.flowRecords) ? device.flowRecords : []
				const deviceName = this.getDeviceName(device, index)
				const color = deviceColors[index % deviceColors.length]

				temperatureRecords.forEach(item => {
					const timeKey = this.formatRecordTime(item.dataTime)
					if (!timeKey) return
					timeSet.add(timeKey)
				})
				flowRecords.forEach(item => {
					const timeKey = this.formatRecordTime(item.dataTime)
					if (!timeKey) return
					timeSet.add(timeKey)
				})

				if (temperatureRecords.length) {
					const name = `${deviceName} 温度`
					deviceSeries.push({
						name,
						type: 'line',
						yAxisIndex: 1,
						color,
						records: temperatureRecords,
						valueKey: 'temperature',
					})
				}

				if (flowRecords.length) {
					const name = `${deviceName} 流量`
					deviceSeries.push({
						name,
						type: 'bar',
						yAxisIndex: 0,
						color,
						records: flowRecords,
						valueKey: 'workingFlow',
					})
				}

				if (temperatureRecords.length || flowRecords.length) {
					deviceLegendItems.push({ name: deviceName, color })
				}
			})

			const axisTimes = Array.from(timeSet).sort(
				(left, right) => this.dayjs(left).valueOf() - this.dayjs(right).valueOf(),
			)
			const series = deviceSeries.map(item => this.buildSeries(item, axisTimes))
			const flowSeries = series.filter(item => item.yAxisIndex === 0)
			const flowStackValues = axisTimes.map((item, index) =>
				flowSeries.reduce((total, seriesItem) => {
					const value = seriesItem.data[index]
					return value === null || value === undefined ? total : total + value
				}, 0),
			)
			const temperatureNumbers = series
				.filter(item => item.yAxisIndex === 1)
				.flatMap(item => item.data)
				.filter(item => item !== null)
			const maxFlowValue = Math.max(200, ...flowStackValues)
			const maxTemperatureValue = Math.max(60, ...temperatureNumbers)

			this.chartData = {
				...this.chartData,
				max: Math.ceil(maxFlowValue),
				temperatureMax: Math.ceil(maxTemperatureValue),
				labels: axisTimes,
				deviceLegendItems,
				series,
			}
			this.renderChart()
		},
		buildSeries(item, axisTimes) {
			const valueMap = new Map()
			item.records.forEach(record => {
				const timeKey = this.formatRecordTime(record.dataTime)
				if (!timeKey) return
				valueMap.set(timeKey, this.getRecordValue(record, item.valueKey))
			})
			const series = {
				name: item.name,
				type: item.type,
				yAxisIndex: item.yAxisIndex,
				data: axisTimes.map(timeKey => (valueMap.has(timeKey) ? valueMap.get(timeKey) : null)),
				itemStyle: {
					color: item.color,
				},
			}

			if (item.type === 'line') {
				return {
					...series,
					smooth: true,
					symbol: 'circle',
					symbolSize: 6,
					lineStyle: {
						width: 1.5,
						color: item.color,
					},
					itemStyle: {
						color: item.color,
						borderColor: '#ffffff',
						borderWidth: 2,
					},
				}
			}

			return {
				...series,
				stack: 'flow',
				barMaxWidth: 8,
				barGap: '20%',
				itemStyle: {
					color: item.color,
					borderType: 'dashed',
				},
			}
		},
		getRecordValue(record, valueKey) {
			if (valueKey === 'workingFlow') {
				return this.toNumberOrNull(record.workingFlow ?? record.standardFlow)
			}
			return this.toNumberOrNull(record[valueKey])
		},
		getDeviceName(device, index) {
			return `CH${device.subDeviceNo ?? index + 1}`
		},
		toNumberOrNull(value) {
			if (value === null || value === undefined || value === '') return null
			const numberValue = Number(value)
			return Number.isNaN(numberValue) ? null : numberValue
		},
		formatRecordTime(value) {
			if (!value) return ''
			return this.dayjs(value).format('YYYY-MM-DD HH:mm:ss')
		},
		formatCollectTime(value) {
			if (!value) return ''
			return this.dayjs(value).format('MM-DD HH:mm')
		},
		formatAxisTime(value) {
			if (!value) return ''
			return this.dayjs(value).format('HH:mm')
		},
		formatTooltip(params) {
			if (!params || !params.length) return ''
			const header = params[0]?.axisValue ? this.formatCollectTime(params[0].axisValue) : '--'
			const lines = params
				.filter(item => item.data !== null && item.data !== undefined)
				.map(item => {
					const unit = item.seriesType === 'bar' ? 'm³/h' : '℃'
					return `${item.marker}${item.seriesName}：${item.data} ${unit}`
				})
			return [header, ...lines].join('<br/>')
		},
		getAxisInterval(ticks) {
			if (!ticks || ticks.length < 2) {
				return null
			}
			return Math.abs(ticks[1] - ticks[0])
		},
		getChartOption() {
			var axisInterval = this.getAxisInterval(this.chartData.gridTicks)

			return {
				animation: false,
				grid: {
					top: 32,
					right: 64,
					bottom: 32,
					left: 58,
				},
				tooltip: {
					trigger: 'axis',
					backgroundColor: 'rgba(15, 23, 42, 0.92)',
					borderWidth: 0,
					textStyle: {
						color: '#f8fafc',
						fontSize: 12,
					},
					formatter: params => this.formatTooltip(params),
				},
				xAxis: {
					type: 'category',
					boundaryGap: true,
					data: this.chartData.labels,
					axisLine: {
						lineStyle: {
							color: '#e2e8f0',
						},
					},
					axisTick: {
						show: false,
					},
					axisLabel: {
						color: '#64748b',
						fontSize: 12,
						margin: 12,
						formatter: value => this.formatAxisTime(value),
					},
				},
				yAxis: [
					{
						type: 'value',
						name: '流量(m³/h)',
						nameLocation: 'end',
						nameGap: 12,
						nameTextStyle: {
							color: '#64748b',
							fontSize: 12,
						},
						min: this.chartData.min,
						max: this.chartData.max,
						interval: axisInterval,
						axisLine: {
							show: false,
						},
						axisTick: {
							show: false,
						},
						axisLabel: {
							color: '#64748b',
							fontSize: 12,
							margin: 12,
						},
						splitLine: {
							show: false,
						},
					},
					{
						type: 'value',
						name: '温度(℃)',
						nameLocation: 'end',
						nameGap: 12,
						nameTextStyle: {
							color: '#64748b',
							fontSize: 12,
						},
						min: this.chartData.min,
						max: this.chartData.temperatureMax,
						axisLine: {
							show: false,
						},
						axisTick: {
							show: false,
						},
						axisLabel: {
							color: '#64748b',
							fontSize: 12,
							margin: 12,
						},
						splitLine: {
							show: false,
						},
					},
				],
				series: this.chartData.series,
			}
		},
		renderChart() {
			if (!this.chartInstance && this.$refs.chartRef) {
				this.chartInstance = echarts.init(this.$refs.chartRef, null, { renderer: 'canvas' })
			}

			if (this.chartInstance) {
				this.chartInstance.setOption(this.getChartOption(), true)
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.section-card {
	position: relative;
	padding: 22px 24px 18px;
	background: var(--panel-bg);
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	box-shadow: var(--panel-shadow);
	backdrop-filter: blur(12px);
}

.section-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 14px;
}

.section-title {
	margin: 0;
	color: var(--title);
	font-size: 17px;
	font-weight: 700;
	letter-spacing: 0.01em;
}

.chart-legend {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-wrap: wrap;
	gap: 12px;
	width: 100%;
}

::v-deep .time-range-picker.el-date-editor--date {
	width: 220px !important;
	max-width: 100%;
}

::v-deep .time-range-picker.el-date-editor {
	display: inline-flex;
	align-items: center;
}

::v-deep .time-range-picker .el-range__icon,
::v-deep .time-range-picker .el-range__close-icon,
::v-deep .time-range-picker .el-range-separator {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	line-height: 1;
}

::v-deep .time-range-picker .el-range-input {
	height: 100%;
	line-height: 1;
}

.chart-shell {
	position: relative;
	padding-top: 4px;
}

.chart-legend {
	margin-bottom: 6px;
}

.chart-legend--devices {
	margin-bottom: 8px;
}

.chart-legend__item {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	font-size: 11px;
	color: var(--muted);
}

.chart-legend__dot {
	width: 12px;
	height: 12px;
	border-radius: 3px;
}

.chart-legend__line {
	display: inline-block;
	width: 12px;
	height: 0;
	border-top: 1px solid var(--muted);
}

.chart-legend__line--dashed {
	border-top-style: dashed;
}

.chart-canvas {
	width: 100%;
	height: 320px;
}

@media (max-width: 900px) {
	.section-head {
		flex-direction: column;
		align-items: flex-start;
	}

	.chart-canvas {
		height: 280px;
	}
}
</style>
